import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    BarChart2, Clock, Activity, Users, FileText 
} from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import { 
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
    BarElement, Title, Tooltip, Legend, Filler 
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const Analytics = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchRealData = async () => {
            try {
                // 1. ЗАГРУЖАЕМ РЕАЛЬНЫЕ ДАННЫЕ ИЗ БАЗЫ
                const [docsRes, patsRes, presRes] = await Promise.all([
                    api.get('/users?role=doctor'),
                    api.get('/patients'),
                    api.get('/prescriptions')
                ]);

                const doctors = docsRes.data || [];
                const patients = patsRes.data || [];
                const prescriptions = presRes.data || [];

                // 2. ПОДГОТОВКА МЕСЯЦЕВ (Последние 6 месяцев)
                const monthNames = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
                const labels = [];
                const docCounts = [0, 0, 0, 0, 0, 0];
                const patCounts = [0, 0, 0, 0, 0, 0];
                const presCounts = [0, 0, 0, 0, 0, 0];

                const now = new Date();
                // Генерируем массив названий месяцев, например ["Янв 2026", "Фев 2026", ...]
                for (let i = 5; i >= 0; i--) {
                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    labels.push(`${monthNames[d.getMonth()]} ${d.getFullYear()}`);
                }

                // Функция для определения, в какой из 6 месяцев попадает дата
                const getMonthIndex = (dateString) => {
                    const d = new Date(dateString);
                    const diffMonths = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
                    if (diffMonths >= 0 && diffMonths <= 5) {
                        return 5 - diffMonths; // 5 - это текущий месяц, 0 - это 5 месяцев назад
                    }
                    return -1;
                };

                // 3. ПОДГОТОВКА ЧАСОВ (Активность в течение дня 0:00 - 23:00)
                const hourLabels = Array.from({length: 24}, (_, i) => `${i}:00`);
                const hourCounts = Array(24).fill(0);

                // 4. ОБРАБОТКА ДАННЫХ
                const processItem = (item, type) => {
                    if (!item.created_at) return;
                    const d = new Date(item.created_at);

                    // Считаем месяцы
                    const mIdx = getMonthIndex(item.created_at);
                    if (mIdx !== -1) {
                        if (type === 'doc') docCounts[mIdx]++;
                        if (type === 'pat') patCounts[mIdx]++;
                        if (type === 'pres') presCounts[mIdx]++;
                    }

                    // Считаем часы активности
                    const hour = d.getHours();
                    hourCounts[hour]++;
                };

                doctors.forEach(d => processItem(d, 'doc'));
                patients.forEach(p => processItem(p, 'pat'));
                prescriptions.forEach(pr => processItem(pr, 'pres'));

                // 5. РАСЧЕТ СРЕДНИХ ЗНАЧЕНИЙ (ЗА 6 МЕСЯЦЕВ)
                const totalDocs = docCounts.reduce((a, b) => a + b, 0);
                const totalPats = patCounts.reduce((a, b) => a + b, 0);
                const totalPres = presCounts.reduce((a, b) => a + b, 0);

                setData({
                    growth: {
                        months: labels,
                        doctors: docCounts,
                        patients: patCounts,
                        prescriptions: presCounts
                    },
                    time_activity: {
                        hours: hourLabels,
                        values: hourCounts
                    },
                    summary: {
                        avg_doctors_growth: totalDocs / 6,
                        avg_patients_growth: totalPats / 6,
                        recipes_per_month: (totalPres / 6).toFixed(1)
                    }
                });

            } catch (error) {
                console.error("Ошибка загрузки аналитики:", error);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchRealData();
    }, []);

    // --- НАСТРОЙКИ ГРАФИКОВ ---

    const growthChartData = {
        labels: data?.growth?.months || [],
        datasets: [
            {
                label: 'Пациенты',
                data: data?.growth?.patients || [],
                borderColor: '#8b5cf6', // Purple
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                tension: 0.4,
                fill: true
            },
            {
                label: 'Рецепты',
                data: data?.growth?.prescriptions || [],
                borderColor: '#f97316', // Orange
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                tension: 0.4,
                fill: true
            },
            {
                label: 'Врачи',
                data: data?.growth?.doctors || [],
                borderColor: '#3b82f6', // Blue
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4,
                fill: true
            }
        ]
    };

    const growthOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { stepSize: 1 } },
            x: { grid: { display: false } }
        }
    };

    const timeChartData = {
        labels: data?.time_activity?.hours || [],
        datasets: [
            {
                label: 'Действия в системе (Регистрации и Рецепты)',
                data: data?.time_activity?.values || [],
                backgroundColor: '#3b82f6',
                borderRadius: 4
            }
        ]
    };

    const timeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { stepSize: 1 } },
            x: { grid: { display: false } }
        }
    };

    if (isLoading) return <div style={{padding:'40px', textAlign:'center', color: '#6b7280'}}>Собираем статистику...</div>;

    return (
        <div>
            {/* ЗАГОЛОВОК */}
            <div className="card" style={{marginBottom: '20px'}}>
                <div className="card-header anl-cart">
                    <h4 className="card-title" style={{display:'flex', alignItems:'center', gap:'10px', fontSize: '20px' }}>
                        <BarChart2 size={24} color="#4f46e5" />
                        Расширенная аналитика
                    </h4>
                    <p className="card-subtitle" style={{ marginTop: '4px' }}>Реальная статистика и тренды вашей системы</p>
                </div>
            </div>

            {/* ГРАФИК РОСТА */}
            <div className="card" style={{marginBottom: '20px'}}>
                <div className="card-header anl-cart border-b" style={{borderBottom:'1px solid #e5e7eb'}}>
                    <h4 className="card-title">Рост системы по месяцам</h4>
                    <p className="card-subtitle">Врачи, пациенты и рецепты за последние 6 месяцев</p>
                </div>
                <div className="card-body pt-4" style={{height: '350px', padding:'20px'}}>
                    <div className="chart-container-lg" style={{height: '100%', width: '100%'}}>
                        <Line data={growthChartData} options={growthOptions} />
                    </div>
                </div>
            </div>

            {/* ГРАФИК ВРЕМЕНИ */}
            <div className="card" style={{marginBottom: '20px'}}>
                <div className="card-header anl-cart border-b" style={{borderBottom:'1px solid #e5e7eb'}}>
                    <h4 className="card-title" style={{display:'flex', alignItems:'center', gap:'8px', color: '#2563eb'}}>
                        <Clock size={20} />
                        Активность по времени суток
                    </h4>
                    <p className="card-subtitle">В какие часы пользователи чаще создают записи</p>
                </div>
                <div className="card-body pt-4" style={{height: '300px', padding:'20px'}}>
                    <div className="chart-container-md" style={{height: '100%', width: '100%'}}>
                        <Bar data={timeChartData} options={timeOptions} />
                    </div>
                </div>
            </div>

            {/* КАРТОЧКИ СВОДКИ (GRID) */}
            <div className="grid-3-col anl-card-footer" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:'20px'}}>
                
                {/* Врачи */}
                <div className="card" style={{background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)', border: '1px solid #bfdbfe', borderRadius: '12px'}}>
                    <div className="card-body flex items-center gap-4 pt-6" style={{display:'flex', alignItems:'center', gap:'15px', padding:'20px'}}>
                        <div className="icon-box" style={{background:'#3b82f6', color:'white', padding:'12px', borderRadius:'10px'}}>
                            <Users size={24} />
                        </div>
                        <div>
                            <p style={{color:'#4b5563', fontSize:'0.85rem', margin: '0 0 4px 0', fontWeight: '500'}}>Средний прирост врачей</p>
                            <p style={{color:'#2563eb', fontSize:'1.5rem', fontWeight:'bold', margin:0}}>
                                +{data?.summary?.avg_doctors_growth?.toFixed(1) || 0} / мес
                            </p>
                        </div>
                    </div>
                </div>

                {/* Пациенты */}
                <div className="card" style={{background: 'linear-gradient(135deg, #f3e8ff 0%, #ffffff 100%)', border: '1px solid #e9d5ff', borderRadius: '12px'}}>
                    <div className="card-body flex items-center gap-4 pt-6" style={{display:'flex', alignItems:'center', gap:'15px', padding:'20px'}}>
                        <div className="icon-box" style={{background:'#9333ea', color:'white', padding:'12px', borderRadius:'10px'}}>
                            <Activity size={24} />
                        </div>
                        <div>
                            <p style={{color:'#4b5563', fontSize:'0.85rem', margin: '0 0 4px 0', fontWeight: '500'}}>Средний прирост пациентов</p>
                            <p style={{color:'#9333ea', fontSize:'1.5rem', fontWeight:'bold', margin:0}}>
                                +{data?.summary?.avg_patients_growth?.toFixed(1) || 0} / мес
                            </p>
                        </div>
                    </div>
                </div>

                {/* Рецепты */}
                <div className="card" style={{background: 'linear-gradient(135deg, #ffedd5 0%, #ffffff 100%)', border: '1px solid #fed7aa', borderRadius: '12px'}}>
                    <div className="card-body flex items-center gap-4 pt-6" style={{display:'flex', alignItems:'center', gap:'15px', padding:'20px'}}>
                        <div className="icon-box" style={{background:'#ea580c', color:'white', padding:'12px', borderRadius:'10px'}}>
                            <FileText size={24} />
                        </div>
                        <div>
                            <p style={{color:'#4b5563', fontSize:'0.85rem', margin: '0 0 4px 0', fontWeight: '500'}}>Выписано рецептов</p>
                            <p style={{color:'#ea580c', fontSize:'1.5rem', fontWeight:'bold', margin:0}}>
                                ~{data?.summary?.recipes_per_month || 0} / мес
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Analytics;